import React, { useState, useCallback } from 'react';
import './Reviews.css';

// Simple initials avatar (no cartoon characters) – traditional, neutral style
const getInitialsAvatarUrl = (name) => {
  const seed = (name || 'User').trim().replace(/\s+/g, ' ');
  return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed)}&backgroundColor=35225a&textColor=ffffff`;
};

const DEFAULT_REVIEWS = [
  {
    id: 1,
    text: 'I appreciate the consistent support and feedback throughout the course. It truly enhances the learning experience',
    rating: 5,
    name: 'Tanishq Soni',
    timeAgo: '1 week ago',
  },
  {
    id: 2,
    text: 'The mentor explains even advanced topics in a very approachable manner. Learning here never feels overwhelming.',
    rating: 5,
    name: 'Rahul.',
    timeAgo: '10 days ago',
  },
  {
    id: 3,
    text: 'Every class is packed with insights that directly connect to industry expectations. The practical exposure is a big plus.',
    rating: 5,
    name: 'Shashwat',
    timeAgo: '2 weeks ago',
  },
  {
    id: 4,
    text: 'I loved the practical approach and interactive teaching style. Doubts are addressed patiently, and learning feels smooth and structured.',
    rating: 5,
    name: 'Shivani Singh',
    timeAgo: '3 weeks ago',
  },
  {
    id: 5,
    text: 'What impressed me most was the clarity in communication and structured teaching methodology',
    rating: 5,
    name: 'Technical Talk',
    timeAgo: '1 month ago',
  },
  {
    id: 6,
    text: 'One of the best learning experiences I’ve had. The training is structured, hands-on, and very practical. Every session adds real confidence and clarity.',
    rating: 5,
    name: 'Hetal S',
    timeAgo: '1 month ago',
  },
  {
    id: 7,
    text: 'The sessions are highly interactive and industry-oriented. I really appreciate how complex topics are simplified with relatable examples. Truly a value-driven learning experience.',
    rating: 5,
    name: 'Megha khandelwal',
    timeAgo: '1 month ago',
  },
  {
    id: 8,
    text: 'Excellent balance between explanation,demonstration ,and practice',
    rating: 5,
    name: 'Aarav Kanthalia',
    timeAgo: '1 month ago',
  },
  {
    id: 9,
    text: 'The training emphasizes problem solving skills, which makes it very career-oriented',
    rating: 5,
    name: 'Yashi jain',
    timeAgo: '1 month ago',
  },
  {
    id: 10,
    text: 'One of the best learning experiences I’ve had. The training is structured, hands-on, and very practical. Every session adds real confidence and clarity.',
    rating: 5,
    name: 'Technical Talk',
    timeAgo: '1 month ago',
  },
];

const StarRating = ({ rating = 5 }) => (
  <div className="reviews-card-stars" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className="reviews-star" data-filled={i <= rating}>
        ★
      </span>
    ))}
  </div>
);

const Reviews = ({
  title = 'Read reviews, learn with confidence.',
  ratingLabel = '4.8/5',
  introText = 'What our learners are saying',
  reviews = DEFAULT_REVIEWS,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [skipTransition, setSkipTransition] = useState(false);
  const cardsToShow = 3;
  // Infinite loop: duplicate 3x and add wrap cards so we can jump from last→first and first→last smoothly
  const duplicatedReviews = [
    reviews[reviews.length - 1],
    ...reviews,
    ...reviews,
    ...reviews,
    reviews[0],
  ].map((r, i) => ({ ...r, id: r.id + '_' + i }));
  const totalCards = duplicatedReviews.length;
  const maxSlide = totalCards - cardsToShow;

  const goPrev = useCallback(() => {
    setSlideIndex((i) => (i <= 0 ? -1 : i - 1));
  }, []);
  const goNext = useCallback(() => {
    setSlideIndex((i) => (i >= maxSlide ? maxSlide + 1 : i + 1));
  }, [maxSlide]);

  const handleTransitionEnd = useCallback(
    (e) => {
      if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
      if (slideIndex === -1) {
        setSkipTransition(true);
        setSlideIndex(maxSlide);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setSkipTransition(false));
        });
      } else if (slideIndex === maxSlide + 1) {
        setSkipTransition(true);
        setSlideIndex(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setSkipTransition(false));
        });
      }
    },
    [slideIndex, maxSlide]
  );

  const trackWidthPercent = (totalCards / cardsToShow) * 100;
  const cardWidthPercent = 100 / totalCards;
  const trackStyle = {
    width: `${trackWidthPercent}%`,
    transform: `translateX(-${(slideIndex * 100) / totalCards}%)`,
    transition: skipTransition ? 'none' : 'transform 0.35s ease-out',
  };
  const cardStyle = { flex: `0 0 ${cardWidthPercent}%` };

  const displaySlide = slideIndex === -1 ? maxSlide : slideIndex === maxSlide + 1 ? 0 : slideIndex;
  const displayMax = reviews.length - cardsToShow;
  const navDisplay = displayMax <= 0 ? '1 / 1' : `${(displaySlide % reviews.length) + 1} / ${reviews.length}`;

  return (
    <section className="reviews" id="reviews" aria-label="Customer reviews">
      <div className="reviews-inner">
        <div className="reviews-left">
          <div className="reviews-quote-icon" aria-hidden="true">"</div>
          <h2 className="reviews-title">{title}</h2>
          <div className="reviews-rating-summary">
            <span className="reviews-rating-value">{ratingLabel}</span>
            <StarRating rating={5} />
            
          </div>
          <p className="reviews-intro">{introText}</p>
          <a
            href="https://share.google/lR6Zq5qH2f3XxaFp9"
            className="reviews-view-all-link"
            target="_blank"
            rel="noreferrer"
          >
            View all reviews on Google
          </a>
          <div className="reviews-nav">
            <button
              type="button"
              className="reviews-nav-btn reviews-nav-prev"
              onClick={goPrev}
              aria-label="Previous reviews"
            >
              ‹
            </button>
            <span className="reviews-nav-dots">{navDisplay}</span>
            <button
              type="button"
              className="reviews-nav-btn reviews-nav-next"
              onClick={goNext}
              aria-label="Next reviews"
            >
              ›
            </button>
          </div>
        </div>
        <div className="reviews-right">
          <div
            className="reviews-slider-track"
            style={trackStyle}
            onTransitionEnd={handleTransitionEnd}
          >
            {duplicatedReviews.map((review) => (
              <article key={review.id} className="reviews-card" style={cardStyle}>
                <p className="reviews-card-text">{review.text}</p>
                <StarRating rating={review.rating} />
                <div className="reviews-card-author">
                  <img
                    src={review.avatar || getInitialsAvatarUrl(review.name)}
                    alt=""
                    className="reviews-card-avatar"
                    width={40}
                    height={40}
                  />
                  <div className="reviews-card-meta">
                    <span className="reviews-card-name">{review.name}</span>
                    <span className="reviews-card-time">{review.timeAgo}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
